import React from "react"
import {pick} from "lodash"
import {H3} from "@irati/ui/src/Typography"
import FormattedMessage, {FormattedCurrency} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import {useBudgetsList, useBudgetsPermissions} from "@irati/wallet/hooks/budget"
import {ValidationButton} from "@irati/ui/src/FormItem"
import Form from "@irati/ui/src/Form"
import {FlexStyles} from "../../../components/styled/FormItems"
import BudgetBar from "../../../components/Budgeting/BudgetBar"
import {EditableBudgetCategories, useBudgetManagement} from "../hooks"
import {Unassigned} from "../shared/Unassigned"
import {useBudgetModals} from "../Modals"
import {FormErrors} from "../shared/FormErrors"
import {Budget} from "./Budget"
import {ManagerBudget} from "./ManagerBudget"

export default function EditDashboard() {
  const {data} = useBudgetsList()
  const {
    state: {budgets}
  } = useBudgetManagement()

  if (!budgets || !data) return null

  const categories = budgets.categories.map((c) => pick(c, ["name", "uuid", "metadata", "percentage"])).concat(budgets.newCategories)

  return (
    <>
      <BudgetBar unassigned={budgets.unassigned} categories={categories} />
      <BudgetsView budgets={budgets} />
      <H3 as="span">
        <Flex as="span" justifyContent="space-between" marginRight={3}>
          <FormattedMessage id="wallet.reward.budget.dashboard.body.total" />
          <FormattedCurrency value={data.total} currency={"RP"} />
        </Flex>
      </H3>
    </>
  )
}

function BudgetsView({budgets}: {budgets: EditableBudgetCategories}) {
  const {canManageCategories} = useBudgetsPermissions()
  const {dispatch} = useBudgetManagement()
  const {unassigned, categories, newCategories} = budgets
  const categoryNames = categories.map(({name, uuid}) => ({name, uuid})).concat(newCategories)

  return (
    <Flex direction="column" flex="1" gap={2}>
      <FlexStyles />

      <Unassigned unassigned={unassigned} />

      <Form>
        <Flex direction="column" flex="1" gap={2} marginTop={2}>
          {categories.map((category) =>
            canManageCategories ? (
              <Budget
                {...category}
                invalidNames={categoryNames.filter((c) => c.uuid !== category.uuid).map((c) => c.name)}
                key={category.uuid}
              />
            ) : (
              <ManagerBudget {...category} key={category.uuid} />
            )
          )}

          {newCategories.map((category) => {
            return (
              <Budget
                {...category}
                invalidNames={categoryNames.filter((c) => c.uuid !== category.uuid).map((c) => c.name)}
                key={category.uuid}
                remove={() => {
                  dispatch({type: "removeBudget", data: category.uuid})
                }}
              />
            )
          })}
          <Footer />
        </Flex>
      </Form>
    </Flex>
  )
}

function Footer() {
  const {canManageCategories} = useBudgetsPermissions()
  const {dispatch, state, validate} = useBudgetManagement()
  const {showConfirm, showCancel} = useBudgetModals()
  const {submitting, dirty} = state

  return (
    <Flex direction="column">
      <Flex>
        <Flex flex="1">
          {canManageCategories && (
            <Button small onClick={() => dispatch({type: "addBudget"})}>
              <FormattedMessage id="wallet.reward.budget.dashboard.cta.edit.addCategory" />
            </Button>
          )}
        </Flex>

        <Flex gap={2}>
          <Button small onClick={() => showCancel()}>
            <FormattedMessage id="platform.button.cancel" />
          </Button>

          <ValidationButton
            apolloState={{loading: submitting}}
            disabled={!dirty}
            primary
            small
            type="submit"
            onClick={() => validate(showConfirm)}>
            <FormattedMessage id="platform.button.save" />
          </ValidationButton>
        </Flex>
      </Flex>
      <FormErrors />
    </Flex>
  )
}
